import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import {
  CarIcon, PhoneIcon, WhatsappLogoIcon, FacebookLogoIcon, InstagramLogoIcon,
  MapPinIcon, EnvelopeSimpleIcon, ArrowRightIcon, StorefrontIcon, CheckCircleIcon,
} from "@phosphor-icons/react";

const clean = (s) => (s || "").trim();
const phoneHref = (p) => "tel:" + (p || "").replace(/[^+\d]/g, "");
const waHref = (p) => "https://wa.me/" + (p || "").replace(/[^\d]/g, "");
const socialHref = (u) => {
  if (!u) return null;
  return /^https?:\/\//i.test(u) ? u : "https://" + u;
};

const badgeForStatus = (s) => s === "reserved"
  ? { label: { ar: "محجوزة", en: "Reserved" }, color: "#FFEA00", bg: "rgba(255,234,0,0.08)" }
  : s === "maintenance"
    ? { label: { ar: "صيانة", en: "Maintenance" }, color: "#FF3B30", bg: "rgba(255,59,48,0.08)" }
    : { label: { ar: "متاحة", en: "Available" }, color: "#39FF14", bg: "rgba(57,255,20,0.08)" };

export const PublicSite = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [cars, setCars] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([api.get("/public/site"), api.get("/public/cars")]);
      setData(s.data);
      setCars(c.data.cars || []);
    })();
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-500 uppercase tracking-[0.3em] text-sm">Loading…</div>;

  const s = data.site || {};
  const stats = data.stats || {};
  const branches = data.branches || [];
  const companyName = clean(s.company_name) || "Abu Shaikha Rent A Car";
  const tagline = clean(s.tagline) || (lang === "ar" ? "أسطول موثوق · أسعار منافسة · خدمة على مدار الساعة" : "Trusted fleet · Competitive rates · 24/7 service");

  const filtered = filter === "all" ? cars : cars.filter((c) => c.status === filter);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 py-4">
          <div className="flex items-center gap-3">
            {s.logo && <img src={s.logo} alt="logo" className="w-10 h-10 object-cover border border-white/10" />}
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Since 2020</div>
              <div className="text-lg sm:text-xl font-bold leading-none">{companyName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Link to="/login" data-testid="site-login-link" className="hidden sm:inline-flex items-center gap-2 border border-white/10 bg-[#121212] hover:bg-[#1C1C1C] px-3 py-2 text-xs uppercase tracking-[0.2em]">
              {t("signIn")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        {s.hero_image && <img src={s.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">/{lang === "ar" ? "شركة تأجير سيارات" : "car rental company"}</div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight max-w-3xl leading-none">
            {companyName}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-xl">{tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {s.whatsapp && <a href={waHref(s.whatsapp)} target="_blank" rel="noreferrer" data-testid="site-whatsapp-btn" className="inline-flex items-center gap-2 bg-[#25D366] text-black px-5 py-3 font-semibold hover:opacity-90"><WhatsappLogoIcon size={18} weight="bold" /> {lang === "ar" ? "احجز عبر واتساب" : "Book on WhatsApp"}</a>}
            {s.phone && <a href={phoneHref(s.phone)} data-testid="site-phone-btn" className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 font-semibold hover:bg-zinc-100"><PhoneIcon size={18} weight="bold" /> {s.phone}</a>}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 border border-white/10">
          <StatCell label={lang === "ar" ? "إجمالي السيارات" : "Total cars"} value={stats.total_cars} testid="stat-total" />
          <StatCell label={lang === "ar" ? "المتاح للتأجير" : "Available"} value={stats.available} color="#39FF14" testid="stat-available" />
          <StatCell label={lang === "ar" ? "المحجوز حالياً" : "Reserved"} value={stats.reserved} color="#FFEA00" testid="stat-reserved" />
          <StatCell label={lang === "ar" ? "الفروع" : "Branches"} value={stats.branches} last testid="stat-branches" />
        </div>
      </section>

      {/* Branches */}
      {branches.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">/02</div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-8">{lang === "ar" ? "الفروع" : "Branches"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((b, i) => (
              <div key={i} data-testid={`branch-card-${i}`} className="border border-white/10 bg-[#121212] p-6 card-hover">
                <div className="flex items-start gap-3 mb-4">
                  {b.logo
                    ? <img src={b.logo} alt="" className="w-12 h-12 object-cover border border-white/10 shrink-0" />
                    : <div className="w-12 h-12 border border-white/10 bg-[#0A0A0A] flex items-center justify-center shrink-0"><StorefrontIcon size={22} weight="bold" style={{ color: "var(--primary-action, #007AFF)" }} /></div>
                  }
                  <div className="min-w-0">
                    <div className="text-lg font-bold truncate">{b.office_name}</div>
                    {b.email && <div className="text-xs text-zinc-500 truncate" dir="ltr">{b.email}</div>}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em]">
                  <span className="text-zinc-500">{lang === "ar" ? "السيارات" : "Cars"}</span>
                  <span className="text-white font-semibold text-base">{b.cars_available}<span className="text-zinc-500">/{b.cars_total}</span></span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Cars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">/03</div>
            <h2 className="text-3xl sm:text-4xl font-bold">{lang === "ar" ? "الأسطول المتاح" : "Available fleet"}</h2>
          </div>
          <div className="inline-flex border border-white/10">
            {["all", "available", "reserved"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`filter-${k}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${filter === k ? "bg-white text-black" : "text-zinc-400 hover:bg-[#1C1C1C]"}`}
              >
                {k === "all" ? (lang === "ar" ? "الكل" : "All") : k === "available" ? (lang === "ar" ? "متاحة" : "Available") : (lang === "ar" ? "محجوزة" : "Reserved")}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="border border-white/10 bg-[#121212] p-10 text-center text-zinc-500">—</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c) => {
              const b = badgeForStatus(c.status);
              return (
                <div key={c.car_id} data-testid={`car-card-${c.car_id}`} className="border border-white/10 bg-[#121212] overflow-hidden card-hover">
                  <div className="aspect-[16/10] bg-[#0A0A0A] relative">
                    {c.image_url
                      ? <img src={c.image_url} alt={`${c.make} ${c.model}`} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><CarIcon size={64} weight="thin" className="text-zinc-700" /></div>
                    }
                    <span className="absolute top-3 end-3 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 border" style={{ color: b.color, borderColor: b.color, backgroundColor: b.bg }}>{b.label[lang]}</span>
                  </div>
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{c.branch_name}</div>
                    <div className="text-xl font-bold mt-1 leading-tight">{c.make} {c.model}</div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono" dir="ltr">{c.plate_number}{c.year ? ` · ${c.year}` : ""}</div>
                    {c.daily_rate && (
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
                        <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">{lang === "ar" ? "اليومي" : "per day"}</span>
                        <span className="text-lg font-bold" style={{ color: "var(--primary-action, #007AFF)" }}>{c.daily_rate} {lang === "ar" ? "ر.ع" : "OMR"}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* About + Contact */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">/04</div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{lang === "ar" ? "عن الشركة" : "About us"}</h2>
          <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
            {clean(s.about) || (lang === "ar"
              ? "شركة أبوشيخة لتأجير السيارات تقدّم خدمات تأجير سيارات موثوقة في السلطنة، بأسطول حديث متنوّع وشبكة فروع في المدن الرئيسية، وخدمة عملاء متواصلة على مدار اليوم."
              : "Abu Shaikha Rent A Car provides trusted vehicle rental services across the region with a modern diverse fleet, branches in major cities, and 24/7 customer support.")}
          </p>
        </div>
        <div className="border border-white/10 bg-[#121212] p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">{lang === "ar" ? "تواصل معنا" : "Contact"}</div>
            <div className="space-y-3">
              {s.phone && <ContactRow icon={PhoneIcon} label={s.phone} href={phoneHref(s.phone)} dir="ltr" />}
              {s.whatsapp && <ContactRow icon={WhatsappLogoIcon} label={s.whatsapp} href={waHref(s.whatsapp)} dir="ltr" color="#25D366" />}
              {s.email && <ContactRow icon={EnvelopeSimpleIcon} label={s.email} href={`mailto:${s.email}`} dir="ltr" />}
              {s.address && <ContactRow icon={MapPinIcon} label={s.address} />}
            </div>
          </div>
          {(s.facebook || s.instagram || s.twitter) && (
            <div className="pt-3 border-t border-white/10">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">{lang === "ar" ? "شبكات التواصل" : "Social"}</div>
              <div className="flex gap-2">
                {s.facebook && <SocialBtn href={socialHref(s.facebook)} icon={FacebookLogoIcon} label="Facebook" testid="site-facebook" />}
                {s.instagram && <SocialBtn href={socialHref(s.instagram)} icon={InstagramLogoIcon} label="Instagram" testid="site-instagram" />}
                {s.twitter && <SocialBtn href={socialHref(s.twitter)} icon={CheckCircleIcon} label="X" testid="site-twitter" />}
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10 py-6 text-center text-xs text-zinc-500">
        © {new Date().getFullYear()} · {lang === "ar" ? "جميع الحقوق محفوظة للمهندس سلطان عبدالعزيز أبوذيبة" : "All rights reserved to Engineer Sultan Abdulaziz Aboudheba"}
      </footer>
    </div>
  );
};

const StatCell = ({ label, value, color, last, testid }) => (
  <div data-testid={testid} className={`p-6 ${last ? "" : "border-e border-white/10"} border-b sm:border-b-0 border-white/10`}>
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</div>
    <div className="text-4xl font-bold mt-2" style={{ color: color || "white" }}>{value ?? "—"}</div>
  </div>
);

const ContactRow = ({ icon: Icon, label, href, color, dir }) => {
  const content = (
    <div className="flex items-center gap-3">
      <div className="p-2 border border-white/10 bg-[#0A0A0A]"><Icon size={16} weight="bold" style={{ color: color || undefined }} /></div>
      <span dir={dir} className="text-sm text-zinc-200 truncate">{label}</span>
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer" className="block hover:opacity-80 transition-opacity">{content}</a> : content;
};

const SocialBtn = ({ href, icon: Icon, label, testid }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testid} title={label} className="inline-flex items-center justify-center w-10 h-10 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] transition-colors">
    <Icon size={18} weight="bold" />
  </a>
);
